import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { userAPI } from '@/lib/api';
import { toast } from 'sonner';
import { getInitials } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
    User, Lock, MapPin, Save, Eye, EyeOff, AlertTriangle, Camera, Loader2, Bell,
    BellRing, BellOff, Mail, Clock, Gavel, MessageCircle, Package, Truck, Sparkles, Tag, UserPlus,
    Heart, ChevronDown, ChevronRight, CheckSquare, Square
} from 'lucide-react';
import TwoFactorSettings from './TwoFactorSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardSettingsTab = ({ user, updateUser }) => {
    const { token } = useAuth();
    const [profileForm, setProfileForm] = useState({
        real_name: user?.real_name || '',
        phone: user?.phone || '',
        shipping_address: user?.shipping_address || '',
        bank_account: user?.bank_account || '',
    });
    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileInputRef = useRef(null);
    
    // Notification preferences state
    const [notifLoading, setNotifLoading] = useState(true);
    const [savingNotif, setSavingNotif] = useState(false);
    const [categories, setCategories] = useState([]);
    
    // Push notification state
    const [pushState, setPushState] = useState('loading'); // loading, unsupported, denied, inactive, active
    const [isTogglingPush, setIsTogglingPush] = useState(false);
    
    // Categories expand/collapse state for "Categoriile mele" UI
    const [expandedCategories, setExpandedCategories] = useState({});
    
    const [notifPrefs, setNotifPrefs] = useState({
        // Global toggles
        push_enabled: true,
        email_enabled: true,
        
        // Auction alerts
        auction_ending_alerts: true,
        auction_ending_push: true,
        auction_ending_email: true,
        auction_ending_times: [60, 30, 15, 5, 1],
        
        outbid_alerts: true,
        outbid_push: true,
        outbid_email: true,
        
        auction_won_alerts: true,
        auction_won_push: true,
        auction_won_email: true,
        
        // New listings
        new_listing_alerts: false,
        new_listing_push: false,
        new_listing_email: false,
        new_listing_digest: 'none',
        
        // Price drops
        price_drop_alerts: true,
        price_drop_push: true,
        price_drop_email: true,
        price_drop_threshold: 10,
        
        // Followers
        new_follower_alerts: true,
        new_follower_push: true,
        new_follower_email: true,
        
        followed_seller_alerts: true,
        followed_seller_push: true,
        followed_seller_email: true,
        
        // Messages
        new_message_alerts: true,
        new_message_push: true,
        new_message_email: true,
        
        // Orders
        order_status_alerts: true,
        order_status_push: true,
        order_status_email: true,
        
        shipping_alerts: true,
        shipping_push: true,
        shipping_email: true,
        
        // Categories
        categories: [],
        subcategories: [],
        
        // Legacy
        min_auction_value: 0,
        max_auction_value: null
    });

    const availableTimes = [
        { value: 60, label: '1 oră' },
        { value: 30, label: '30 min' },
        { value: 15, label: '15 min' },
        { value: 5, label: '5 min' },
        { value: 1, label: '1 min' }
    ];

    useEffect(() => {
        fetchNotifPreferences();
        fetchCategories();
    }, []);

    const fetchNotifPreferences = async () => {
        try {
            const response = await fetch(`${API_URL}/api/notifications/preferences`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setNotifPrefs(prev => ({ ...prev, ...data }));
            }
        } catch (error) {
            console.error('Error fetching preferences:', error);
        } finally {
            setNotifLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/api/notifications/categories-list`);
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };
    
    // Toggle expand/collapse for a category
    const toggleCategoryExpand = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const handleSaveNotifPrefs = async () => {
        setSavingNotif(true);
        try {
            const response = await fetch(`${API_URL}/api/notifications/preferences`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(notifPrefs)
            });
            
            if (response.ok) {
                toast.success('Preferințele de notificare au fost salvate!');
            } else {
                toast.error('Eroare la salvarea preferințelor');
            }
        } catch (error) {
            toast.error('Eroare la salvarea preferințelor');
        } finally {
            setSavingNotif(false);
        }
    };

    const toggleTime = (time) => {
        setNotifPrefs(prev => {
            const times = prev.auction_ending_times || [];
            if (times.includes(time)) {
                return { ...prev, auction_ending_times: times.filter(t => t !== time) };
            } else {
                return { ...prev, auction_ending_times: [...times, time].sort((a, b) => b - a) };
            }
        });
    };

    const toggleCategory = (categoryId) => {
        setNotifPrefs(prev => {
            const cats = prev.categories || [];
            if (cats.includes(categoryId)) {
                return { ...prev, categories: cats.filter(c => c !== categoryId) };
            } else {
                return { ...prev, categories: [...cats, categoryId] };
            }
        });
    };

    // Check push notification state
    const checkPushState = useCallback(async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            setPushState('unsupported');
            return;
        }

        if (Notification.permission === 'denied') {
            setPushState('denied');
            return;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            setPushState(subscription ? 'active' : 'inactive');
        } catch {
            setPushState('inactive');
        }
    }, []);

    useEffect(() => {
        checkPushState();
    }, [checkPushState]);

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const activatePush = async () => {
        setIsTogglingPush(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                setPushState('denied');
                toast.error('Ai blocat notificările. Activează-le din setările browserului.');
                return;
            }

            const vapidResponse = await fetch(`${API_URL}/api/notifications/vapid-public-key`);
            const { publicKey } = await vapidResponse.json();

            const registration = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            });

            const p256dhKey = subscription.getKey('p256dh');
            const authKey = subscription.getKey('auth');

            await fetch(`${API_URL}/api/notifications/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    endpoint: subscription.endpoint,
                    keys: {
                        p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(p256dhKey))),
                        auth: btoa(String.fromCharCode.apply(null, new Uint8Array(authKey)))
                    }
                })
            });

            setPushState('active');
            toast.success('Notificările push au fost activate!');
        } catch (err) {
            console.error('Error activating push:', err);
            toast.error('Eroare la activarea notificărilor');
        } finally {
            setIsTogglingPush(false);
        }
    };

    const deactivatePush = async () => {
        setIsTogglingPush(true);
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                await subscription.unsubscribe();
                
                await fetch(`${API_URL}/api/notifications/subscribe`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ endpoint: subscription.endpoint })
                });
            }

            setPushState('inactive');
            toast.success('Notificările push au fost dezactivate');
        } catch (err) {
            console.error('Error deactivating push:', err);
            toast.error('Eroare la dezactivarea notificărilor');
        } finally {
            setIsTogglingPush(false);
        }
    };

    const testPush = async () => {
        try {
            const response = await fetch(`${API_URL}/api/notifications/test-push`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            
            if (response.ok && data.sent_count > 0) {
                toast.success(`Notificare de test trimisă!`);
            } else {
                toast.error('Niciun dispozitiv înregistrat');
            }
        } catch {
            toast.error('Eroare la trimiterea notificării de test');
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file
        if (!file.type.startsWith('image/')) {
            toast.error('Te rog selectează o imagine');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Imaginea trebuie să fie mai mică de 2MB');
            return;
        }
        
        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const res = await userAPI.uploadProfilePhoto(formData);
            toast.success('Poza de profil a fost actualizată!');
            if (updateUser) {
                updateUser({ 
                    picture: res.data.profile_picture_url,
                    profile_picture_url: res.data.profile_picture_url 
                });
            }
            // Force page reload to update all components with new picture
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la încărcarea pozei');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await userAPI.updateMe(profileForm);
            toast.success('Profilul a fost salvat cu succes!');
            if (updateUser) updateUser(profileForm);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la salvarea profilului');
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            toast.error('Parolele nu coincid');
            return;
        }
        
        if (passwordForm.new_password.length < 8) {
            toast.error('Parola trebuie să aibă minim 8 caractere');
            return;
        }
        
        setSavingPassword(true);
        try {
            await userAPI.changePassword({
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password,
            });
            toast.success('Parola a fost schimbată cu succes!');
            setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la schimbarea parolei');
        } finally {
            setSavingPassword(false);
        }
    };

    return (
        <div className="grid gap-6 md:grid-cols-2 items-stretch max-w-md md:max-w-none mx-auto">
            {/* Profile Picture */}
            <Card className="rounded-2xl md:col-span-2">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Camera className="w-5 h-5" />
                        Poză de profil
                    </CardTitle>
                    <CardDescription>
                        Adaugă o poză de profil pentru a fi recunoscut de ceilalți utilizatori
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-6">
                        <div className="relative">
                            <Avatar className="w-24 h-24 border-2 border-border">
                                <AvatarImage 
                                    src={user?.profile_picture_url || user?.picture} 
                                    alt={user?.name || user?.username}
                                />
                                <AvatarFallback className="text-2xl bg-primary/10">
                                    {getInitials(user?.name || user?.username)}
                                </AvatarFallback>
                            </Avatar>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingPhoto}
                                className="absolute bottom-0 right-0 p-2 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                            >
                                {uploadingPhoto ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Camera className="w-4 h-4" />
                                )}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                className="hidden"
                            />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium">{user?.name || user?.username}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Formatul recomandat: JPG sau PNG, max 2MB
                            </p>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-3 rounded-full"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingPhoto}
                            >
                                {uploadingPhoto ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Se încarcă...
                                    </>
                                ) : (
                                    <>
                                        <Camera className="w-4 h-4 mr-2" />
                                        Schimbă poza
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Profile Settings */}
            <Card className="rounded-2xl flex flex-col">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Date personale
                    </CardTitle>
                    <CardDescription>
                        Actualizează informațiile de contact și livrare
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                    <form onSubmit={handleSaveProfile} className="h-full flex flex-col">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="real_name">Nume complet</Label>
                                <Input
                                    id="real_name"
                                    value={user?.name || user?.username || ''}
                                    disabled
                                    className="bg-muted cursor-not-allowed"
                                />
                                <p className="text-xs text-muted-foreground">Numele nu poate fi modificat</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Telefon</Label>
                                <Input
                                    id="phone"
                                    value={profileForm.phone}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="+40 700 000 000"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="shipping_address" className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    Adresă livrare
                                </Label>
                                <Textarea
                                    id="shipping_address"
                                    value={profileForm.shipping_address}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, shipping_address: e.target.value }))}
                                    placeholder="Strada, Număr, Oraș, Cod Poștal, Județ"
                                    rows={3}
                                />
                            </div>
                        </div>
                        <div className="flex-grow"></div>
                        <div className="pt-4">
                            <Button type="submit" className="w-full rounded-full" disabled={savingProfile}>
                                <Save className="w-4 h-4 mr-2" />
                                {savingProfile ? 'Se salvează...' : 'Salvează modificările'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
            
            {/* Password Change - only for users with password (not OAuth-only) */}
            {user?.has_password && (
            <Card className="rounded-2xl flex flex-col">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Lock className="w-5 h-5" />
                        Schimbare parolă
                    </CardTitle>
                    <CardDescription>
                        Actualizează parola contului tău
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                    <form onSubmit={handleChangePassword} className="h-full flex flex-col">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="current_password">Parola curentă</Label>
                                <div className="relative">
                                    <Input
                                        id="current_password"
                                        type={showPasswords.current ? 'text' : 'password'}
                                        value={passwordForm.current_password}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                                    >
                                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="new_password">Parola nouă</Label>
                                <div className="relative">
                                    <Input
                                        id="new_password"
                                        type={showPasswords.new ? 'text' : 'password'}
                                        value={passwordForm.new_password}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                                        placeholder="Minim 8 caractere"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                                    >
                                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm_password">Confirmă parola nouă</Label>
                                <div className="relative">
                                    <Input
                                        id="confirm_password"
                                        type={showPasswords.confirm ? 'text' : 'password'}
                                        value={passwordForm.confirm_password}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                                        placeholder="Repetă parola nouă"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                                    >
                                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            {passwordForm.new_password && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                                <p className="text-sm text-red-500 flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    Parolele nu coincid
                                </p>
                            )}
                        </div>
                        <div className="flex-grow"></div>
                        <div className="pt-4">
                            <Button 
                                type="submit" 
                                className="w-full rounded-full" 
                                disabled={savingPassword || !passwordForm.current_password || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
                            >
                                <Lock className="w-4 h-4 mr-2" />
                                {savingPassword ? 'Se schimbă...' : 'Schimbă parola'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
            )}

            {/* Expanded Notification Settings */}
            <Card className="rounded-2xl md:col-span-2">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Bell className="w-5 h-5" />
                        Setări Notificări
                    </CardTitle>
                    <CardDescription>
                        Configurează cum și când vrei să primești notificări
                    </CardDescription>
                    
                    {/* Quick Push Actions - moved here */}
                    <div className="flex items-center gap-3 pt-2 justify-end">
                        {pushState === 'loading' ? (
                            <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm text-muted-foreground">Se verifică...</span>
                            </div>
                        ) : pushState === 'unsupported' ? (
                            <div className="flex items-center gap-2 text-orange-600">
                                <BellOff className="w-4 h-4" />
                                <span className="text-sm">Browserul nu suportă notificări push</span>
                            </div>
                        ) : pushState === 'denied' ? (
                            <div className="flex items-center gap-2 text-red-600">
                                <BellOff className="w-4 h-4" />
                                <span className="text-sm">Notificările sunt blocate în browser</span>
                            </div>
                        ) : (
                            <>
                                {pushState === 'active' && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={testPush}
                                    >
                                        <Bell className="w-4 h-4 mr-2" />
                                        Testează push
                                    </Button>
                                )}
                                
                                <Button
                                    variant={pushState === 'active' ? 'destructive' : 'default'}
                                    size="sm"
                                    onClick={pushState === 'active' ? deactivatePush : activatePush}
                                    disabled={isTogglingPush}
                                >
                                    {isTogglingPush ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : pushState === 'active' ? (
                                        <BellOff className="w-4 h-4 mr-2" />
                                    ) : (
                                        <BellRing className="w-4 h-4 mr-2" />
                                    )}
                                    {pushState === 'active' ? 'Dezactivează push' : 'Activează push'}
                                </Button>
                            </>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {notifLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                    ) : (
                        <>
                            {/* Separator line */}
                            <div className="border-b"></div>
                            
                            {/* Column Headers - below separator, sticky on scroll */}
                            <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center pb-2 sticky top-16 bg-white/95 backdrop-blur-sm z-10 py-2 -mx-6 px-6 shadow-sm">
                                <div></div>
                                <div className="flex flex-col items-center w-16">
                                    <Bell className="w-4 h-4 text-primary mb-1" />
                                    <span className="text-xs text-muted-foreground text-center">In-App</span>
                                </div>
                                <div className="flex flex-col items-center w-16">
                                    <Bell className="w-4 h-4 text-blue-500 mb-1" />
                                    <span className="text-xs text-muted-foreground text-center">Push</span>
                                </div>
                                <div className="flex flex-col items-center w-16">
                                    <Mail className="w-4 h-4 text-green-500 mb-1" />
                                    <span className="text-xs text-muted-foreground text-center">Email</span>
                                </div>
                            </div>

                            {/* Licitații */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                    <Gavel className="w-4 h-4" />
                                    Licitații
                                </h4>
                                
                                {/* Licitații care expiră */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Licitații care expiră</Label>
                                        <p className="text-sm text-muted-foreground">Primește alerte înainte de terminarea licitațiilor</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.auction_ending_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, auction_ending_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.auction_ending_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, auction_ending_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.auction_ending_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, auction_ending_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                                
                                {notifPrefs.auction_ending_alerts && (
                                    <div className="pl-4 border-l-2 border-primary/20 ml-2">
                                        <Label className="text-sm text-muted-foreground mb-2 block">
                                            <Clock className="w-4 h-4 inline mr-2" />
                                            Cu cât timp înainte?
                                        </Label>
                                        <div className="flex flex-wrap gap-2">
                                            {availableTimes.map(time => (
                                                <Button
                                                    key={time.value}
                                                    variant={notifPrefs.auction_ending_times?.includes(time.value) ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => toggleTime(time.value)}
                                                >
                                                    {time.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Ai fost depășit */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Ai fost depășit</Label>
                                        <p className="text-sm text-muted-foreground">Când cineva licitează mai mult decât tine</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.outbid_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, outbid_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.outbid_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, outbid_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.outbid_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, outbid_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                                
                                {/* Ai câștigat */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Ai câștigat</Label>
                                        <p className="text-sm text-muted-foreground">Când câștigi o licitație</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.auction_won_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, auction_won_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.auction_won_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, auction_won_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.auction_won_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, auction_won_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Listări și Prețuri */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                    <Sparkles className="w-4 h-4" />
                                    Listări și Prețuri
                                </h4>
                                
                                {/* Listări noi */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Listări noi</Label>
                                        <p className="text-sm text-muted-foreground">Când apar produse noi în categoriile tale</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_listing_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_listing_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_listing_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_listing_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_listing_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_listing_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                                
                                {/* Scăderi de preț */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Scăderi de preț</Label>
                                        <p className="text-sm text-muted-foreground">Când un produs favorit are reducere</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.price_drop_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, price_drop_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.price_drop_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, price_drop_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.price_drop_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, price_drop_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Urmăritori */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                    <UserPlus className="w-4 h-4" />
                                    Urmăritori
                                </h4>
                                
                                {/* Urmăritor nou */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Urmăritor nou</Label>
                                        <p className="text-sm text-muted-foreground">Când cineva începe să te urmărească</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_follower_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_follower_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_follower_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_follower_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_follower_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_follower_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                                
                                {/* Listări de la vânzători urmăriți */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Vânzători urmăriți</Label>
                                        <p className="text-sm text-muted-foreground">Când un vânzător urmărit adaugă ceva nou</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.followed_seller_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, followed_seller_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.followed_seller_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, followed_seller_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.followed_seller_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, followed_seller_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Mesaje și Comenzi */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                    <MessageCircle className="w-4 h-4" />
                                    Mesaje și Comenzi
                                </h4>
                                
                                {/* Mesaje noi */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Mesaje noi</Label>
                                        <p className="text-sm text-muted-foreground">Când primești un mesaj nou</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_message_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_message_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_message_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_message_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.new_message_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, new_message_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                                
                                {/* Status comenzi */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Status comenzi</Label>
                                        <p className="text-sm text-muted-foreground">Actualizări despre comenzile tale</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.order_status_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, order_status_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.order_status_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, order_status_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.order_status_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, order_status_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                                
                                {/* Livrare */}
                                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center py-2">
                                    <div>
                                        <Label className="font-medium">Livrare</Label>
                                        <p className="text-sm text-muted-foreground">Actualizări despre livrări</p>
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.shipping_alerts}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, shipping_alerts: checked }))}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.shipping_push}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, shipping_push: checked }))}
                                            disabled={!notifPrefs.push_enabled}
                                        />
                                    </div>
                                    <div className="w-16 flex justify-center">
                                        <Checkbox
                                            checked={notifPrefs.shipping_email}
                                            onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, shipping_email: checked }))}
                                            disabled={!notifPrefs.email_enabled}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Categoriile mele - folosit pentru pagina /my-domains și notificări listări noi */}
                            {categories.length > 0 && (
                                <>
                                    <Separator />
                                    <div className="space-y-4" data-testid="my-domains-settings">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                                <Heart className="w-4 h-4 text-primary" />
                                                Categoriile mele
                                            </h4>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        const allCats = categories.map(c => c.category_id);
                                                        const allSubs = categories.flatMap(c => c.subcategories?.map(s => s.subcategory_id) || []);
                                                        setNotifPrefs(prev => ({ ...prev, categories: allCats, subcategories: allSubs }));
                                                    }}
                                                    className="text-xs h-7"
                                                >
                                                    <CheckSquare className="w-3 h-3 mr-1" />
                                                    Toate
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setNotifPrefs(prev => ({ ...prev, categories: [], subcategories: [] }))}
                                                    className="text-xs h-7"
                                                >
                                                    <Square className="w-3 h-3 mr-1" />
                                                    Niciuna
                                                </Button>
                                            </div>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            Categoriile selectate vor fi afișate pe pagina "Categoriile mele" și vei primi notificări despre listări noi din acestea.
                                        </p>
                                        <div className="space-y-3">
                                            {categories.map(cat => {
                                                const isExpanded = expandedCategories[cat.category_id];
                                                const selectedSubsCount = cat.subcategories?.filter(s => 
                                                    notifPrefs.subcategories?.includes(s.subcategory_id)
                                                ).length || 0;
                                                const totalSubs = cat.subcategories?.length || 0;
                                                
                                                return (
                                                    <div key={cat.category_id} className="border rounded-lg overflow-hidden">
                                                        <div 
                                                            className="flex items-center gap-3 p-3 bg-gray-50/50 hover:bg-gray-100/50 transition-colors cursor-pointer"
                                                            onClick={() => toggleCategoryExpand(cat.category_id)}
                                                        >
                                                            <button type="button" className="text-muted-foreground">
                                                                {isExpanded ? (
                                                                    <ChevronDown className="w-4 h-4" />
                                                                ) : (
                                                                    <ChevronRight className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                            
                                                            <div onClick={(e) => e.stopPropagation()}>
                                                                <Checkbox
                                                                    id={`cat-${cat.category_id}`}
                                                                    checked={notifPrefs.categories?.includes(cat.category_id)}
                                                                    onCheckedChange={() => {
                                                                        const cats = notifPrefs.categories || [];
                                                                        const subs = notifPrefs.subcategories || [];
                                                                        const subcatIds = cat.subcategories?.map(s => s.subcategory_id) || [];
                                                                        
                                                                        if (cats.includes(cat.category_id)) {
                                                                            setNotifPrefs(prev => ({
                                                                                ...prev,
                                                                                categories: cats.filter(c => c !== cat.category_id),
                                                                                subcategories: subs.filter(s => !subcatIds.includes(s))
                                                                            }));
                                                                        } else {
                                                                            setNotifPrefs(prev => ({
                                                                                ...prev,
                                                                                categories: [...cats, cat.category_id],
                                                                                subcategories: [...new Set([...subs, ...subcatIds])]
                                                                            }));
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            
                                                            <Label 
                                                                htmlFor={`cat-${cat.category_id}`}
                                                                className="font-medium cursor-pointer flex-1"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {cat.name}
                                                            </Label>
                                                            
                                                            {totalSubs > 0 && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    {selectedSubsCount}/{totalSubs}
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {isExpanded && cat.subcategories?.length > 0 && (
                                                            <div className="p-3 pt-0 border-t bg-white">
                                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pt-3">
                                                                    {cat.subcategories.map(sub => (
                                                                        <div key={sub.subcategory_id} className="flex items-center gap-2">
                                                                            <Checkbox
                                                                                id={`sub-${sub.subcategory_id}`}
                                                                                checked={notifPrefs.subcategories?.includes(sub.subcategory_id)}
                                                                                onCheckedChange={() => {
                                                                                    const subs = notifPrefs.subcategories || [];
                                                                                    const cats = notifPrefs.categories || [];
                                                                                    
                                                                                    if (subs.includes(sub.subcategory_id)) {
                                                                                        const newSubs = subs.filter(s => s !== sub.subcategory_id);
                                                                                        const categorySubIds = cat.subcategories?.map(s => s.subcategory_id) || [];
                                                                                        const hasAnySubSelected = categorySubIds.some(id => newSubs.includes(id));
                                                                                        
                                                                                        setNotifPrefs(prev => ({
                                                                                            ...prev,
                                                                                            categories: hasAnySubSelected ? cats : cats.filter(c => c !== cat.category_id),
                                                                                            subcategories: newSubs
                                                                                        }));
                                                                                    } else {
                                                                                        setNotifPrefs(prev => ({
                                                                                            ...prev,
                                                                                            categories: cats.includes(cat.category_id) ? cats : [...cats, cat.category_id],
                                                                                            subcategories: [...subs, sub.subcategory_id]
                                                                                        }));
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <Label 
                                                                                htmlFor={`sub-${sub.subcategory_id}`}
                                                                                className="text-sm text-muted-foreground cursor-pointer truncate"
                                                                            >
                                                                                {sub.name}
                                                                            </Label>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Save Button */}
                            <div className="pt-4">
                                <Button 
                                    onClick={handleSaveNotifPrefs} 
                                    disabled={savingNotif}
                                    className="w-full rounded-full"
                                >
                                    {savingNotif ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Se salvează...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4 mr-2" />
                                            Salvează preferințele de notificare
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Two-Factor Authentication Settings */}
            <div className="md:col-span-2">
                <TwoFactorSettings user={user} updateUser={updateUser} />
            </div>
        </div>
    );
};

export default DashboardSettingsTab;
